/**
 * LA GALERÍA
 * =============================================================================
 * Las fotos que el administrador pone desde el panel.
 *
 * -----------------------------------------------------------------------------
 * DE DÓNDE SALEN LAS FOTOS
 * -----------------------------------------------------------------------------
 * La página le pregunta al servidor del panel qué fotos hay, y las pinta. No hay
 * ninguna foto escrita aquí: se añaden y se quitan desde el panel, sin tocar el
 * código y sin volver a publicar la página.
 *
 * -----------------------------------------------------------------------------
 * SI ALGO FALLA, NO ROMPE NADA
 * -----------------------------------------------------------------------------
 * Si el servidor no contesta —porque no hay internet o porque está caído— la
 * sección simplemente NO se muestra. No sale un hueco vacío, ni un error, ni una
 * foto rota: la página se ve como si la galería no existiera.
 *
 * Es lo correcto: la galería es un adorno, y un adorno no puede romper la página.
 */

import { useEffect, useState } from 'react';
import { CONFIG } from '../config';
import { Icono } from '../componentes/Iconos';
import { Revelar } from '../componentes/Piezas';
import { useIdioma } from '../i18n/useIdioma';
import { useAjustes } from '../i18n/useAjustes';

/** Una foto de la galería. */
interface Foto {
  id: number;
  titulo: string | null;
  url: string;
}

export function Galeria() {
  const { textos, idioma } = useIdioma();
  const { valor, activo } = useAjustes();
  const t = textos.galeria;

  const [fotos, setFotos] = useState<Foto[]>([]);
  /** La foto que se está viendo en grande. */
  const [ampliada, setAmpliada] = useState<Foto | null>(null);

  // --- Se piden las fotos al servidor ---
  useEffect(() => {
    if (!CONFIG.galeria.activa) {
      return;
    }

    let vivo = true;

    fetch(CONFIG.galeria.api)
      .then((r) => (r.ok ? r.json() : { fotos: [] }))
      .then((d) => {
        if (vivo) setFotos(Array.isArray(d?.fotos) ? d.fotos : []);
      })
      // Si falla, se queda sin fotos y la sección no se muestra.
      .catch(() => {
        if (vivo) setFotos([]);
      })


    return () => {
      vivo = false;
    };
  }, []);

  // --- Se cierra la foto ampliada con Escape ---
  useEffect(() => {
    if (!ampliada) return;

    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAmpliada(null);
    };
    window.addEventListener('keydown', alTeclear);

    // Y se bloquea el desplazamiento de atrás mientras está abierta.
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = anterior;
    };
  }, [ampliada]);

  /* La sección se enseña SIEMPRE, aunque no haya fotos.
     -----------------------------------------------------------------------------
     Antes desaparecía cuando estaba vacía, y eso hacía imposible saber que existía:
     ponías una foto en el panel y no sabías dónde iba a salir.

     Ahora se ve el sitio donde van, con su título. Si no hay ninguna, se explica
     cómo se ponen. */
  if (!activo('galeria_activa')) return null;

  return (
    <section id="galeria" className="seccion seccion--galeria">
      <div className="caja">
        <Revelar>
          <p className="antetitulo">{t.etiqueta}</p>
          <h2 className="titulo-seccion titulo-seccion--medio">
            <span className="oro">
              {valor(idioma === 'en' ? 'galeria_titulo_en' : 'galeria_titulo_es', t.titulo)}
            </span>
          </h2>
          <p className="bajada">{t.texto}</p>
        </Revelar>

        {fotos.length === 0 ? (
          <Revelar>
            <div className="galeria__vacia">
              <Icono nombre="paisaje" tam={40} />
              <p>{t.vacia}</p>
            </div>
          </Revelar>
        ) : (
        <Revelar>
          <div className="galeria">
            {fotos.map((f) => (
              <button
                type="button"
                className="galeria__foto"
                key={f.id}
                onClick={() => setAmpliada(f)}
                aria-label={f.titulo || t.ampliar}
              >
                <img src={f.url} alt={f.titulo || ''} loading="lazy" decoding="async" />
                {f.titulo && <span className="galeria__pie">{f.titulo}</span>}
              </button>
            ))}
          </div>
        </Revelar>
        )}
      </div>

      {/* La foto en grande. */}
      {ampliada && (
        <div
          className="galeria__velo"
          role="dialog"
          aria-modal="true"
          aria-label={ampliada.titulo || t.ampliar}
        >
          <button
            type="button"
            className="galeria__fondo"
            onClick={() => setAmpliada(null)}
            aria-label={t.cerrar}
            tabIndex={-1}
          />
          <button
            type="button"
            className="galeria__cerrar"
            onClick={() => setAmpliada(null)}
            aria-label={t.cerrar}
          >
            <Icono nombre="cerrar" tam={24} />
          </button>
          <img className="galeria__grande" src={ampliada.url} alt={ampliada.titulo || ''} />
          {ampliada.titulo && <p className="galeria__titulo">{ampliada.titulo}</p>}
        </div>
      )}
    </section>
  );
}
